import { useEffect, useState } from "react";
import apiClient from "../utils/apiClient";
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  InputAdornment,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Add, Edit, Delete, People, Search as SearchIcon, Groups, ArrowBack } from "@mui/icons-material";
import PageLayout from "../components/admin/PageLayout";

const Committees = () => {
  const navigate = useNavigate();
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [committeeToDelete, setCommitteeToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchCommittees = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("committee", {
        params: { createdByOnly: true, type: "auction" },
      });
      if (response.data.success) {
        setCommittees(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching committees:", error);
      showSnackbar("Error fetching committees", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommittees();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleAddCommittee = () => {
    navigate("/add-committee");
  };

  const handleEditCommittee = (id) => {
    navigate(`/edit-committee/${id}`);
  };

  const handleViewMembers = (id) => {
    navigate(`/committee/${id}`);
  };

  const handleDeleteClick = (id) => {
    setCommitteeToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setCommitteeToDelete(null);
  };

  const handleDeleteCommittee = async () => {
    if (!committeeToDelete) return;
    try {
      const response = await apiClient.delete(`committee/${committeeToDelete}`);
      if (response.data.success) {
        setCommittees((prev) => prev.filter((c) => c.id !== committeeToDelete));
        showSnackbar("Committee deleted successfully");
      } else {
        showSnackbar(response.data.message || "Failed to delete committee", "error");
      }
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || "Error deleting committee",
        "error"
      );
    } finally {
      handleDeleteDialogClose();
    }
  };

  const displayType = (type) => {
    if (!type) return "";
    const t = String(type).toLowerCase();
    return t === "auction" ? "Auction" : t.charAt(0).toUpperCase() + t.slice(1);
  };

  return (
    <PageLayout
      title="Auction Committees"
      subtitle="Create and manage auction committees. View members, edit, or delete."
      action={
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <Button startIcon={<ArrowBack />} onClick={() => navigate("/committees")} size="small">
            Back to Committees
          </Button>
          <TextField
            variant="outlined"
            placeholder="Search…"
            size="small"
            sx={{
              minWidth: 220,
              "& .MuiOutlinedInput-root": { bgcolor: "background.paper", borderRadius: 2 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "action.active" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleAddCommittee}>
            Add Committee
          </Button>
        </Box>
      }
    >
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
          <CircularProgress size={48} sx={{ color: "primary.main" }} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Table size="medium" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {committees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                    <Groups sx={{ fontSize: 48, color: "action.disabled", display: "block", mx: "auto", mb: 1 }} />
                    <Typography color="text.secondary">No committees yet. Create one to get started.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                committees.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography fontWeight={500}>{row.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={displayType(row.type)} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleViewMembers(row.id)} title="Members">
                        <People />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleEditCommittee(row.id)} title="Edit">
                        <Edit />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(row.id)} title="Delete">
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
        <DialogTitle>Delete Committee</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this committee? All members will be removed. This cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteCommittee} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default Committees;
